import React from 'react';

// =============================================================================
// PRIMITIVE UNION TYPES
// These restrict field values to a known set of strings.
// TypeScript will error at compile-time if any code assigns a value outside
// these unions, preventing typos and invalid API payloads.
// =============================================================================

/** The physical facility categories a role can be scoped to. Values are driven by the
 * GET /location-types API — e.g. 'Distribution Center', 'Manufacturing', 'Warehouse'. */
export type RoleLocationType = string;

/**
 * Which app modules this role appears in (multi-select checkboxes in the form).
 * 'Observations' → shows in observation-type workflows.
 * 'Labor Optimization' → shows in the DLO scheduling module.
 */
export type RoleAvailability = 'Observations' | 'Labor Optimization';
export type OrganizationType = 'PBNA' | 'FLNA' | '1NA';
export type ThreePLStatus = 'Yes' | 'No' | 'Any';
export type LaborRoleType = 'DSD' | 'Non-DSD' | 'Fixed';

/** Whether the role is active and visible in the app modules. */
export type RoleStatus = 'Enabled' | 'Disabled';

// =============================================================================
// CORE DATA SHAPE — what the API returns & what lives in Redux
// =============================================================================

/**
 * A single warehouse role record as stored in Redux and returned from the API.
 * Arrays are used for availableIn and locTypes because the form uses
 * multi-select — one role can belong to multiple modules and location types.
 */
export interface Role {
  /** Server-generated unique identifier. */
  id: string;
  /** Human-readable role label shown in dropdowns and table. */
  name: string;
  /**
   * The vpZone key used as the path param for DELETE /fixed-hour-roles/{vpZone}.
   * Equals `id` / `name` for Labor Optimization roles.
   */
  vpZone?: string;
  /** Which modules this role appears in (Observations, Labor Optimization, or both). */
  availableIn: RoleAvailability[];
  /** Which facility types this role is scoped to. */
  locTypes: RoleLocationType[];
  /** Controls visibility in the app — toggled via the Status switch in the table. */
  status: RoleStatus;
  /** Optional scoping fields shown in Add/Edit form. */
  organizationTypes?: OrganizationType[];
  threePlStatus?: ThreePLStatus;
  roleType?: LaborRoleType;
  /** VP Zone selections for DSD roles — multi-select. */
  vpZones?: string[];
  /** Optional reference counts for the informational banner in edit mode. */
  kfaReferenceCount?: number;
  activeLaborPlanCount?: number;
  /** ISO timestamp set by the server on creation — read-only, never sent in payloads. */
  createdAt?: string;
  /** ISO timestamp set by the server on last update — read-only, never sent in payloads. */
  updatedAt?: string;
}

// =============================================================================
// FORM VALUES — what the Add / Edit modal collects and sends to the thunk
// Intentionally omits id, createdAt, updatedAt — those are server-controlled.
// Keeping this separate from Role prevents accidentally sending id: undefined
// on a create call, which would cause API validation errors.
// =============================================================================

export interface RoleFormValues {
  /** User-entered role name (required, max 15 chars). */
  name: string;
  /** At least one module must be checked (form validation enforced in RoleFormModal). */
  availableIn: RoleAvailability[];
  /** At least one location type must be selected. */
  locTypes: RoleLocationType[];
  /** Status defaults to 'Enabled' on Add; carried from the existing record on Edit. */
  status: RoleStatus;
  /** Required multi-select. */
  organizationTypes: OrganizationType[];
  /** Required single-select. */
  threePlStatus: ThreePLStatus | '';
  /** Required only when Labor Optimization is selected. */
  roleType?: LaborRoleType;
  /**
   * VP Zone selections — multi-select, shown only when roleType === 'DSD'.
   * Sent as an array of strings in the create/update request payload.
   */
  vpZones?: string[];
}

// =============================================================================
// COLUMN FILTER STATE
// Mirrors how the HTML reference tracks active column filters.
// Set<string> is used (not array) for O(1) membership checks when filtering rows.
// Partial<Record<K, V>> means a key only exists when that column has ≥1 active filter,
// so we can cheaply check: Object.keys(filters).length === 0 → no active filters.
// =============================================================================

/**
 * The filterable column keys — tells TypeScript which columns support filtering.
 * Used as keys in RoleFilters to keep filter state and column definitions in sync.
 */
export type RoleFilterKey =
  | 'name'
  | 'availableIn'
  | 'locTypes'
  | 'organizationTypes'
  | 'threePlStatus'
  | 'roleType'
  | 'status';

/**
 * Active filter selections per column.
 * Each key maps to a Set of checked values for that column's dropdown.
 * A key is absent (not just empty) when no filter is active for that column.
 */
export type RoleFilters = Partial<Record<RoleFilterKey, Set<string>>>;

// =============================================================================
// MODAL STATE
// All modal visibility is centralised in one object — this is the same pattern
// used in the DLO module (useModalState.ts). A single useState<RoleModalState>
// in the page replaces 6+ separate boolean useState calls.
// editingRole / deletingRole carry the target row so handlers don't need to
// look it up again from the roles array.
// =============================================================================

export interface RoleModalState {
  // ── Add / Edit form modal ──────────────────────────────────────────────────
  /** Controls Add/Edit modal visibility. */
  formVisible: boolean;
  /** 'add' → modal title is "Add New Role"; 'edit' → "Edit Role". */
  formMode: 'add' | 'edit';
  /** The role being edited. null when mode is 'add'. */
  editingRole: Role | null;

  // ── Delete confirmation modal ──────────────────────────────────────────────
  /** Controls the delete-confirm modal visibility. */
  deleteVisible: boolean;
  /** The role queued for deletion. null when no deletion is pending. */
  deletingRole: Role | null;
}

/**
 * Zero-state for RoleModalState.
 * Used on initialisation and as the reset value after every modal action.
 * Import this constant instead of duplicating the object in multiple files.
 */
export const INITIAL_MODAL_STATE: RoleModalState = {
  formVisible: false,
  formMode: 'add',
  editingRole: null,
  deleteVisible: false,
  deletingRole: null,
};

// =============================================================================
// COMPONENT PROP INTERFACES
// Defined here (before any component is written) to make the parent → child
// contract explicit and enforce it at compile-time across all files.
// =============================================================================

/** Props for the top-level RoleManagementPage screen. */
export interface RoleManagementPageProps {
  /** Called when the user presses the breadcrumb / back button. */
  onBack: () => void;
}

/** Props for the RoleTable component (pure display, receives all needed state). */
export interface RoleTableProps {
  /** Current page of roles from the API; may be filtered/searched. */
  roles: Role[];
  /** Shows a loading skeleton when true (during initial fetch or refetch). */
  isLoading: boolean;
  /** Pagination metadata. */
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
  /** Current sort state (column + direction). */
  sort: {
    sortBy: 'roleName' | 'modifiedAt';
    sortDirection: 'asc' | 'desc';
  };
  /** Filter options returned by API response metadata. */
  filterOptions: {
    availableIn: string[];
    locationTypes: string[];
    organizationTypes: string[];
    threePlStatuses: string[];
    roleTypes: string[];
    statuses: string[];
  };
  /** Triggered when a row is clicked — opens the Edit form modal in the parent. */
  onRowClick: (role: Role) => void;
  /** Called when Apply Filters is clicked; receives filter/sort/page params. */
  onApplyFilters: (params: {
    page: number;
    roleName?: string;
    statuses?: string;
    availableIns?: string;
    locationTypes?: string;
    organizationTypes?: string;
    threePlStatuses?: string;
    roleTypes?: string;
    sortBy: 'roleName' | 'modifiedAt';
    sortDirection: 'asc' | 'desc';
  }) => void;
  /** Called when a column header is clicked for sorting. */
  onSortChange: (sortBy: 'roleName' | 'modifiedAt', sortDirection: 'asc' | 'desc') => void;
  /** Called when Reset All is clicked. */
  onReset: () => void;
}

/** Props for the RoleFormModal (Add / Edit). */
export interface RoleFormModalProps {
  visible: boolean;
  /** Determines modal title and whether the form is pre-filled. */
  mode: 'add' | 'edit';
  /** null in 'add' mode; populated with the role being edited in 'edit' mode. */
  initialValues: Role | null;
  /** Disables the Save button while the createRole / updateRole thunk is in-flight. */
  isSaving: boolean;
  /** Called with collected form values on Save button press (after local validation). */
  onSave: (values: RoleFormValues) => void;
  /** Called on Cancel button press or modal backdrop tap. */
  onCancel: () => void;
  /**
   * Location type options from GET /location-types.
   * When provided, replaces the hardcoded default list in the form.
   */
  locationTypes?: string[];
  /** Full role list for duplicate name validation in Add/Edit. */
  existingRoles?: Role[];
}

// =============================================================================
// HOOK PROP INTERFACE
// useRoleManagementHandlers receives these three things:
// - dispatch  → to dispatch thunks (createRole, updateRole, toggleRoleStatus)
// - modalState → to read which modal is open and which role is targeted
// - setModalState → to open / close modals after thunk resolution
// React.Dispatch<React.SetStateAction<T>> is the exact TypeScript type of a
// useState setter, ensuring we pass it correctly (not a plain function).
// =============================================================================

export interface UseRoleManagementHandlersProps {
  dispatch: any;
  modalState: RoleModalState;
  setModalState: React.Dispatch<React.SetStateAction<RoleModalState>>;
}
